import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WizardSelect from './WizardSelect';
import type { RequiredErrors } from '../../utils/requiredValidation';
import type { LookupItem } from '../../mocks/criminalMasterData';
import {
  categoryShowsInstituteType,
  isInstituteTypeLocked,
  isOtherCorporateInstitute,
  isOtherCorporateInstituteSelected,
  type InstituteTypeDraftFields,
  type InstituteTypeValue,
} from '../../utils/instituteTypeRules';

type Props = {
  idPrefix: string;
  optional?: boolean;
  draft: InstituteTypeDraftFields;
  setDraft?: React.Dispatch<React.SetStateAction<InstituteTypeDraftFields>>;
  editable: boolean;
  errors: RequiredErrors;
  onClearError?: (key: string) => void;
  fileCategoryId: string;
  subFileCategoryId: string;
  fileCategories: LookupItem[];
  subCategories: LookupItem[];
  corporateInstitutes: LookupItem[];
};

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="ccr-err" style={{ marginTop: 4 }}>
      {message}
    </div>
  );
}

export function InstituteTypeSection({
  idPrefix,
  optional = false,
  draft,
  setDraft,
  editable,
  errors,
  onClearError,
  fileCategoryId,
  subFileCategoryId,
  fileCategories,
  subCategories,
  corporateInstitutes,
}: Props) {
  const { t } = useTranslation();
  const visible = categoryShowsInstituteType(fileCategoryId, fileCategories);

  const showOtherInstituteName = useMemo(
    () =>
      isOtherCorporateInstituteSelected(
        draft.corporateInstituteId,
        corporateInstitutes,
        draft.corporateInstituteName,
      ),
    [draft.corporateInstituteId, draft.corporateInstituteName, corporateInstitutes],
  );

  const corporateOptions = useMemo(() => {
    const byId = new Map(corporateInstitutes.map((c) => [c.id, { id: c.id, label: c.name }]));
    if (draft.corporateInstituteId && !byId.has(draft.corporateInstituteId)) {
      byId.set(draft.corporateInstituteId, {
        id: draft.corporateInstituteId,
        label: draft.corporateInstituteName?.trim() || draft.corporateInstituteId,
      });
    }
    return Array.from(byId.values());
  }, [corporateInstitutes, draft.corporateInstituteId, draft.corporateInstituteName]);

  if (!visible) return null;

  const locked = isInstituteTypeLocked(fileCategoryId, subFileCategoryId, fileCategories, subCategories);
  const readOnly = !editable;
  const showCorporate = draft.instituteType === 'corporation';

  const setInstituteType = (instituteType: InstituteTypeValue) => {
    if (readOnly || locked || !setDraft) return;
    setDraft((d) => ({
      ...d,
      instituteType,
      corporateInstituteId: instituteType === 'corporation' ? d.corporateInstituteId : '',
      corporateInstituteName: instituteType === 'corporation' ? d.corporateInstituteName : '',
      corporateInstituteOther: instituteType === 'corporation' ? d.corporateInstituteOther : '',
      corporateInstituteContactName: '',
      corporateInstitutePrefix: '',
    }));
    onClearError?.('instituteType');
    if (instituteType === 'government') {
      onClearError?.('corporateInstituteId');
      onClearError?.('corporateInstituteOther');
    }
  };

  return (
    <>
      <div className="ccr-fg" id={`${idPrefix}-instituteType`} style={{ marginBottom: 14 }}>
        <label>
          {t('wizard.fields.instituteType')} {optional ? null : <span className="req">*</span>}
        </label>
        <div className="ccr-radio-row">
          <label className="ccr-radio">
            <input
              type="radio"
              name={`${idPrefix}-instituteType`}
              checked={draft.instituteType === 'government'}
              disabled={readOnly || locked}
              onChange={() => setInstituteType('government')}
            />
            <span>{t('wizard.fields.governmentDepartment')}</span>
          </label>
          <label className="ccr-radio">
            <input
              type="radio"
              name={`${idPrefix}-instituteType`}
              checked={draft.instituteType === 'corporation'}
              disabled={readOnly || locked}
              onChange={() => setInstituteType('corporation')}
            />
            <span>{t('wizard.fields.corporation')}</span>
          </label>
        </div>
        {editable ? <FieldError message={errors.instituteType} /> : null}
      </div>

      {showCorporate ? (
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {t('wizard.fields.corporateInstitute')} <span className="req">*</span>
            </label>
            <WizardSelect
              id={`${idPrefix}-corporateInstitute`}
              hasError={editable && Boolean(errors.corporateInstituteId)}
              value={draft.corporateInstituteId}
              disabled={readOnly}
              clearable={!readOnly}
              placeholder={t('wizard.placeholders.selectCorporateInstitute')}
              options={corporateOptions}
              onChange={(id) => {
                if (readOnly || !setDraft) return;
                const item = corporateInstitutes.find((c) => c.id === id);
                const name = item?.name ?? corporateOptions.find((o) => o.id === id)?.label ?? '';
                const isOther = isOtherCorporateInstitute(id, name, item?.code);
                setDraft((d) => ({
                  ...d,
                  corporateInstituteId: id,
                  corporateInstituteName: name,
                  corporateInstituteOther: isOther ? d.corporateInstituteOther : '',
                  corporateInstituteContactName: '',
                  corporateInstitutePrefix: '',
                }));
                onClearError?.('corporateInstituteId');
                if (!isOther) onClearError?.('corporateInstituteOther');
              }}
            />
            {editable ? <FieldError message={errors.corporateInstituteId} /> : null}
          </div>
          {showOtherInstituteName ? (
            <div className="ccr-fg">
              <label>
                {t('wizard.fields.otherInstituteName')} <span className="req">*</span>
              </label>
              <input
                id={`${idPrefix}-corporateOther`}
                className={`ccr-inp${editable && errors.corporateInstituteOther ? ' inv' : ''}`}
                type="text"
                maxLength={255}
                value={draft.corporateInstituteOther}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  if (readOnly || !setDraft) return;
                  setDraft((d) => ({ ...d, corporateInstituteOther: e.target.value.slice(0, 255) }));
                  onClearError?.('corporateInstituteOther');
                }}
                placeholder={t('wizard.placeholders.enterOtherInstituteName')}
              />
              {editable ? <FieldError message={errors.corporateInstituteOther} /> : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
